import { authedExchange, authedGet, authedPost } from '../../api/http';
import { readDiscoveryDetail, readDiscoveryList } from '../catalog/discoveryReader';
import { prerenderInput } from '../prerender/buildInput';
import { readOptionalString, readRecord } from '../readers';
import { SemanticRequestError } from '../requestError';
import type { RequestSnapshot } from '../requests/model';
import { readRequestResult, readRequestSnapshot } from '../requests/repository';
import type { ScrapeResult, Scraper } from './model';
import { m } from '../../paraglide/messages';

export class ExecutionRequestError extends SemanticRequestError {
  readonly name = 'ExecutionRequestError';
}

const executionRequest = async <T>(request: () => Promise<unknown>, reader: (value: unknown) => T): Promise<T> => {
  try {
    return reader(await request());
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    if (error instanceof ExecutionRequestError) throw error;
    throw new ExecutionRequestError(error);
  }
};

export const readScrapeResult = (value: unknown): ScrapeResult => {
  const result = readRequestResult(value);
  const row = value as Record<string, unknown>;
  return {
    ...result,
    ...(row.resultId === undefined ? {} : { resultId: readOptionalString(row.resultId, 'execution result id') }),
    ...(row.jqError === undefined ? {} : { jqError: readOptionalString(row.jqError, 'execution jq error') }),
  };
};

export const fetchScrapers = (): Promise<Scraper[]> =>
  executionRequest(() => authedGet<unknown>('/v1/apis'), readDiscoveryList);

export const fetchScraper = (sku: string): Promise<Scraper> =>
  executionRequest(() => authedGet<unknown>(`/v1/apis/${encodeURIComponent(sku)}`), readDiscoveryDetail);

const executeScraper = async (
  operation: Pick<Scraper, 'slug' | 'method' | 'path'>,
  input: unknown,
  signal?: AbortSignal,
): Promise<unknown> => {
  const response = await authedExchange<unknown>(operation.method, operation.path, input, {
    headers: {
      'Idempotency-Key': crypto.randomUUID(),
      Prefer: 'respond-async',
    },
    signal,
  });
  if (response.status === 200) {
    const row = readRecord(response.body, 'execution response');
    if (Object.prototype.hasOwnProperty.call(row, 'output')) return row;
    const snapshot = readRequestSnapshot(row);
    requireSnapshotSKU(snapshot, operation.slug);
    return completedDurableResult(snapshot);
  }
  if (response.status !== 202) throw new Error(`Unexpected execution status ${response.status}`);

  let snapshot = readRequestSnapshot(response.body);
  if (snapshot.status !== 'queued' && snapshot.status !== 'running') {
    throw new Error('Invalid accepted durable request');
  }
  requireSnapshotSKU(snapshot, operation.slug);
  const requestId = snapshot.requestId;
  const location = response.headers.get('Location');
  if (location !== `/v1/requests/${encodeURIComponent(requestId)}`) {
    throw new Error('Invalid durable request location');
  }
  if (response.headers.get('Retry-After') !== String(snapshot.retryAfterSeconds)) {
    throw new Error('Invalid durable retry delay');
  }

  for (;;) {
    await waitToPoll(snapshot.retryAfterSeconds, signal);
    const poll = await authedExchange<unknown>('GET', location, undefined, { signal });
    if (poll.status !== 200) throw new Error(`Unexpected durable poll status ${poll.status}`);
    const next = readRequestSnapshot(poll.body);
    if (next.requestId !== requestId || next.sku !== operation.slug) {
      throw new Error('Invalid durable request identity');
    }
    if (next.status !== 'queued' && next.status !== 'running') return completedDurableResult(next);
    snapshot = next;
  }
};

const requireSnapshotSKU = (snapshot: RequestSnapshot, sku: string): void => {
  if (snapshot.sku !== sku) throw new Error('Invalid durable request identity');
};

const completedDurableResult = (snapshot: RequestSnapshot): unknown => {
  if (snapshot.status === 'succeeded') {
    if (snapshot.resultExpired) {
      throw new DurableFailure(
        'result_expired',
        'The completed result is no longer available.',
        m.dashfix_error_result_expired,
      );
    }
    return snapshot.result;
  }
  if (snapshot.status === 'failed') {
    const { code } = snapshot.error;
    throw new DurableFailure(code, `Durable request failed: ${code}`, () => m.dashfix_error_durable_failed({ code }));
  }
  if (snapshot.status === 'expired') {
    throw new DurableFailure(
      'request_timeout',
      'Durable request expired before completion.',
      m.dashfix_error_durable_expired,
    );
  }
  throw new Error('Invalid completed durable request');
};

/** A durable request that ended without a result. The message stays English,
 *  because it reaches error tracking; `display` is the page-language sentence
 *  `executionErrorMessage` shows. */
class DurableFailure extends Error {
  readonly code: string;
  readonly display: () => string;

  constructor(code: string, message: string, display: () => string) {
    super(message);
    this.code = code;
    this.display = display;
  }
}

const waitToPoll = (seconds: number, signal?: AbortSignal): Promise<void> => {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(finish, seconds * 1_000);
    signal?.addEventListener('abort', abort, { once: true });
    function finish() {
      signal?.removeEventListener('abort', abort);
      resolve();
    }
    function abort() {
      clearTimeout(timer);
      reject(signal?.reason ?? new DOMException('The request was aborted', 'AbortError'));
    }
  });
};

export const runScraper = (
  operation: Pick<Scraper, 'slug' | 'method' | 'path'>,
  input: unknown,
  signal?: AbortSignal,
): Promise<ScrapeResult> =>
  executionRequest(
    () => executeScraper(operation, input, signal),
    readScrapeResult,
  );

export const executionErrorMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof ExecutionRequestError)) return fallback;
  if (error.status !== undefined) {
    return m.dashfix_error_gateway_status({ status: String(error.status), message: error.message });
  }
  if (error.code === undefined) return fallback;
  return error.cause instanceof DurableFailure ? error.cause.display() : error.message;
};

/** The two schemas an endpoint page renders. Everything else `/public/try/{sku}/schema`
 *  returns already reached the page through the catalog. */
export type EndpointSchemas = Pick<Scraper, 'inputSchema' | 'outputSchema'>;

/**
 * Public execution remains a distinct policy: it is house-funded and must carry
 * a Turnstile token, even though it shares the execution boundary.
 *
 * During a prerender crawl the schemas come from the build's OpenAPI projection
 * instead. One request per SKU is precisely what the build input exists to
 * prevent, and the projected schemas are the same authored documents this
 * endpoint serves - `/openapi.json` and `/public/try/{sku}/schema` both publish
 * `server/internal/registry/assets/schemas/`. Live requests are untouched.
 */
export const fetchPublicTrySchema = (sku: string): Promise<EndpointSchemas> => {
  const projected = (prerenderInput('apiSpec') as Record<string, EndpointSchemas> | undefined)?.[sku];
  if (projected) return Promise.resolve(projected);
  return executionRequest(
    () => authedGet<unknown>(`/public/try/${encodeURIComponent(sku)}/schema`),
    readDiscoveryDetail,
  );
};

export const runPublicTry = (sku: string, input: Record<string, unknown>, turnstileToken: string): Promise<unknown> =>
  executionRequest(
    () =>
      authedPost<unknown>(`/public/try/${encodeURIComponent(sku)}`, {
        ...input,
        turnstileToken,
      }),
    (value) => value,
  );
